/**
 * Text Input Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 */
import React, { useState } from "react";

import { SwapHoriz as SwapHorizIcon } from "@mui/icons-material";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";

import { FieldSchema } from "../../types";
import { formatFieldValue } from "../../utils/type-utils";
import {
  guessPrefix,
  isIdField,
  isPrefixedId,
  isUuid,
  prefixedIdToUuid,
  toggleIdFormat,
  uuidToPrefixedId,
} from "../../utils/uuid-utils";

interface TextInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const TextInput: React.FC<TextInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const formattedValue = formatFieldValue(value, field.type);
  const [showUuidHelper, setShowUuidHelper] = useState(false);

  // Check if this field should show UUID conversion option
  const shouldShowUuidToggle =
    isIdField(field.name) &&
    typeof formattedValue === "string" &&
    formattedValue.length > 0 &&
    (isPrefixedId(formattedValue) || isUuid(formattedValue));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleUuidToggle = () => {
    if (typeof formattedValue === "string") {
      const toggledValue = toggleIdFormat(formattedValue, field.name);
      onChange(toggledValue);
      setShowUuidHelper(true);
      setTimeout(() => setShowUuidHelper(false), 2000);
    }
  };

  const getUuidToggleTooltip = (): string => {
    if (typeof formattedValue !== "string") return "";

    if (isPrefixedId(formattedValue)) {
      const uuid = prefixedIdToUuid(formattedValue);
      return uuid ? `Convert to UUID: ${uuid}` : "Convert to UUID format";
    } else if (isUuid(formattedValue)) {
      const prefix = guessPrefix(field.name);
      const prefixed = uuidToPrefixedId(formattedValue, prefix);
      return prefixed
        ? `Convert to ${prefix} format: ${prefixed}`
        : "Convert to prefixed format";
    }

    return "Toggle ID format";
  };

  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        name={field.name}
        label={field.name}
        value={formattedValue}
        onChange={handleChange}
        error={!!error && touched}
        helperText={error && touched ? error : field.description}
        required={!field.isOptional}
        fullWidth
        size="small"
        variant="outlined"
        margin="none"
        InputProps={{
          endAdornment: shouldShowUuidToggle ? (
            <Tooltip title={getUuidToggleTooltip()}>
              <IconButton
                onClick={handleUuidToggle}
                size="small"
                edge="end"
                aria-label="toggle UUID format"
                sx={{ mr: -1 }}
              >
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined,
        }}
        sx={{
          "& .MuiFormHelperText-root": {
            margin: "4px 0 0 0",
          },
        }}
      />
      {showUuidHelper && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            bgcolor: "success.light",
            color: "success.contrastText",
            p: 0.5,
            fontSize: "0.75rem",
            borderRadius: 1,
            zIndex: 1,
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(-4px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          ID format converted successfully!
        </Box>
      )}
    </Box>
  );
};
